// src/TarotCards.js
import React, { useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";

/* =========================================================
   THEME TOKENS
========================================================= */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel look */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO (About.js-consistent)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/tarot-cards`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;  // page cover image
const logoWebp = `${siteUrl}/icons/logo.webp`;    // organization logo

const metaDescription =
  "Explore all seventy eight Tarot cards by suit with clear images and practical meanings. Learn the archetypes of the Major Arcana and the everyday lessons of Wands, Cups, Swords, and Pentacles.";

const megaKeywords = `
  tarot, tarot reading, tarot card meanings, free tarot, 
    online tarot reading, accurate tarot, instant tarot, daily tarot, 
    love tarot, relationship tarot, break-up tarot, ex back tarot, twin flame tarot, soulmate tarot, marriage tarot, 
    career tarot, job tarot, money tarot, business tarot, health tarot (entertainment), yes or no tarot, one card reading, 
    three card spread, five card spread, Celtic Cross spread, past present future tarot, upright meanings, reversed meanings, 
    major arcana meanings, minor arcana meanings, wands suit meaning, cups suit meaning, swords suit meaning, 
    pentacles suit meaning, how to read tarot, beginner tarot guide, advanced tarot guide, tarot tips and spreads, 
    tarot in English, tarot en Español, tarot em Português, tarot en Français, tarot auf Deutsch, tarocchi in Italiano, 
    tarot Türkçe, تاروت عربي, टैरो हिंदी, ٹروٹ اردو, tarot বাংলা, tarot मराठी, tarot தமிழ், tarot తెలుగు, tarot ગુજરાતી, 
    tarot ਪੰਜਾਬੀ, tarot فارسی, tarot עברית, tarot 中文, tarot 粤语, tarot 日本語, tarot 한국어, tarot Bahasa Indonesia, 
    tarot Melayu, tarot Tagalog, tarot Tiếng Việt, tarot ไทย, USA tarot reading, UK tarot reading, Canada tarot reading, 
    Australia tarot reading, New Zealand tarot reading, India tarot reading, Pakistan tarot reading, Bangladesh tarot reading, 
    Sri Lanka tarot reading, Nepal tarot reading, Philippines tarot reading, Malaysia tarot reading, Singapore tarot reading, 
    Indonesia tarot reading, Thailand tarot reading, Vietnam tarot reading, Japan tarot reading, South Korea tarot reading, 
    China tarot reading, Hong Kong tarot reading, Taiwan tarot reading, UAE tarot reading, Saudi Arabia tarot reading, 
    Qatar tarot reading, Kuwait tarot reading, Oman tarot reading, Egypt tarot reading, Morocco tarot reading, 
    South Africa tarot reading, Kenya tarot reading, Nigeria tarot reading, Ghana tarot reading, Ethiopia tarot reading, 
    Turkey tarot reading, Iran tarot reading, Israel tarot reading, Germany tarot reading, France tarot reading, 
    Spain tarot reading, Portugal tarot reading, Italy tarot reading, Netherlands tarot reading, Belgium tarot reading, 
    Switzerland tarot reading, Austria tarot reading, Sweden tarot reading, Norway tarot reading, Denmark tarot reading, 
    Finland tarot reading, Poland tarot reading, Czech Republic tarot reading, Slovakia tarot reading, Hungary tarot reading, 
    Romania tarot reading, Bulgaria tarot reading, Greece tarot reading, Croatia tarot reading, Serbia tarot reading, 
    Mexico tarot reading, Brazil tarot reading, Argentina tarot reading, Chile tarot reading, Colombia tarot reading, 
    Peru tarot reading, tarot near me, online psychic tarot, oracle card reading, angel card reading, moon reading, 
    zodiac tarot, Aries tarot, Taurus tarot, Gemini tarot, Cancer tarot, Leo tarot, Virgo tarot, Libra tarot, 
    Scorpio tarot, Sagittarius tarot, Capricorn tarot, Aquarius tarot, Pisces tarot, tarot today, tarot tonight, 
    no signup tarot, mobile tarot, fast tarot results
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Smooth offset scroll helper (same as WhatsTarot)
========================================================= */
function scrollWithOffset(hash, smooth = true) {
  if (!hash) return;
  const el = document.querySelector(hash);
  if (!el) return;

  const navH = document.getElementById("site-nav")?.offsetHeight || 64; // fallback
  const extra = 8;
  const y = window.scrollY + el.getBoundingClientRect().top - navH - extra;

  const opts = smooth ? { top: Math.max(0, y), behavior: "smooth" } : { top: Math.max(0, y) };
  window.scrollTo(opts);
}

/* =========================================================
   Grouping (keeps your explicit slug lists)
========================================================= */
function useGroupedCards() {
  return useMemo(() => {
    const groups = {
      "Major Arcana": [
        "the-fool-tarot-card-reading-meaning",
        "the-magician-tarot-card-reading-meaning",
        "the-high-priestess-tarot-card-reading-meaning",
        "the-empress-tarot-card-reading-meaning",
        "the-emperor-tarot-card-reading-meaning",
        "the-hierophant-tarot-card-reading-meaning",
        "the-lovers-tarot-card-reading-meaning",
        "the-chariot-tarot-card-reading-meaning",
        "strength-tarot-card-reading-meaning",
        "the-hermit-tarot-card-reading-meaning",
        "wheel-of-fortune-tarot-card-reading-meaning",
        "justice-tarot-card-reading-meaning",
        "the-hanged-man-tarot-card-reading-meaning",
        "death-tarot-card-reading-meaning",
        "temperance-tarot-card-reading-meaning",
        "the-devil-tarot-card-reading-meaning",
        "the-tower-tarot-card-reading-meaning",
        "the-star-tarot-card-reading-meaning",
        "the-moon-tarot-card-reading-meaning",
        "the-sun-tarot-card-reading-meaning",
        "judgement-tarot-card-reading-meaning",
        "the-world-tarot-card-reading-meaning",
      ],
      Wands: [
        "ace-of-wands-tarot-card-reading-meaning",
        "two-of-wands-tarot-card-reading-meaning",
        "three-of-wands-tarot-card-reading-meaning",
        "four-of-wands-tarot-card-reading-meaning",
        "five-of-wands-tarot-card-reading-meaning",
        "six-of-wands-tarot-card-reading-meaning",
        "seven-of-wands-tarot-card-reading-meaning",
        "eight-of-wands-tarot-card-reading-meaning",
        "nine-of-wands-tarot-card-reading-meaning",
        "ten-of-wands-tarot-card-reading-meaning",
        "page-of-wands-tarot-card-reading-meaning",
        "knight-of-wands-tarot-card-reading-meaning",
        "queen-of-wands-tarot-card-reading-meaning",
        "king-of-wands-tarot-card-reading-meaning",
      ],
      Cups: [
        "ace-of-cups-tarot-card-reading-meaning",
        "two-of-cups-tarot-card-reading-meaning",
        "three-of-cups-tarot-card-reading-meaning",
        "four-of-cups-tarot-card-reading-meaning",
        "five-of-cups-tarot-card-reading-meaning",
        "six-of-cups-tarot-card-reading-meaning",
        "seven-of-cups-tarot-card-reading-meaning",
        "eight-of-cups-tarot-card-reading-meaning",
        "nine-of-cups-tarot-card-reading-meaning",
        "ten-of-cups-tarot-card-reading-meaning",
        "page-of-cups-tarot-card-reading-meaning",
        "knight-of-cups-tarot-card-reading-meaning",
        "queen-of-cups-tarot-card-reading-meaning",
        "king-of-cups-tarot-card-reading-meaning",
      ],
      Swords: [
        "ace-of-swords-tarot-card-reading-meaning",
        "two-of-swords-tarot-card-reading-meaning",
        "three-of-swords-tarot-card-reading-meaning",
        "four-of-swords-tarot-card-reading-meaning",
        "five-of-swords-tarot-card-reading-meaning",
        "six-of-swords-tarot-card-reading-meaning",
        "seven-of-swords-tarot-card-reading-meaning",
        "eight-of-swords-tarot-card-reading-meaning",
        "nine-of-swords-tarot-card-reading-meaning",
        "ten-of-swords-tarot-card-reading-meaning",
        "page-of-swords-tarot-card-reading-meaning",
        "knight-of-swords-tarot-card-reading-meaning",
        "queen-of-swords-tarot-card-reading-meaning",
        "king-of-swords-tarot-card-reading-meaning",
      ],
      Pentacles: [
        "ace-of-pentacles-tarot-card-reading-meaning",
        "two-of-pentacles-tarot-card-reading-meaning",
        "three-of-pentacles-tarot-card-reading-meaning",
        "four-of-pentacles-tarot-card-reading-meaning",
        "five-of-pentacles-tarot-card-reading-meaning",
        "six-of-pentacles-tarot-card-reading-meaning",
        "seven-of-pentacles-tarot-card-reading-meaning",
        "eight-of-pentacles-tarot-card-reading-meaning",
        "nine-of-pentacles-tarot-card-reading-meaning",
        "ten-of-pentacles-tarot-card-reading-meaning",
        "page-of-pentacles-tarot-card-reading-meaning",
        "knight-of-pentacles-tarot-card-reading-meaning",
        "queen-of-pentacles-tarot-card-reading-meaning",
        "king-of-pentacles-tarot-card-reading-meaning",
      ],
    };

    return Object.fromEntries(
      Object.entries(groups).map(([group, slugs]) => [
        group,
        Object.values(cardData).filter((c) => slugs.includes(c.slug)),
      ])
    );
  }, []);
}

/* =========================================================
   JSON-LD (CollectionPage + ItemList + FAQPage)
========================================================= */
function schemaGraphFor(groupedCards, faqEntities) {
  const items = Object.values(groupedCards).flat();
  const itemListElement = items.map((c, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: c.name,
    url: `${siteUrl}/tarot-card/${c.slug}`,
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoWebp, width: 512, height: 512 },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Tarot Cards | Meanings and Symbols | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage, // site-wide OG cover (consistent with About.js)
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Tarot Cards", item: pageUrl },
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#all-cards`,
        name: "All 78 Tarot Cards",
        itemListElement,
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqEntities,
      },
    ],
  };
}

/* =========================================================
   Friendly suit introductions
========================================================= */
const suitIntro = (group) =>
  group === "Major Arcana"
    ? "The Major Arcana presents twenty two archetypes that describe turning points in a life. These cards speak to identity, purpose, change, and spiritual development. They set the tone for the reading and often describe themes that extend beyond daily choices."
    : group === "Wands"
    ? "Wands focuses on initiative, passion, creativity, and leadership. The suit helps you act on ideas, build momentum, and navigate the energy required to bring a vision into form."
    : group === "Cups"
    ? "Cups explores feeling, intuition, relationships, compassion, and healing. The suit helps you understand emotional needs and create harmony with yourself and others."
    : group === "Swords"
    ? "Swords addresses thought, truth, clarity, challenge, and communication. The suit helps you choose honest language and make wise decisions when there is tension."
    : "Pentacles looks at work, study, resources, stability, and material well being. The suit helps you create progress that lasts and treat time and energy with respect.";

export default function TarotCards() {
  const groupedCards = useGroupedCards();

  // Build FAQ once and embed in JSON-LD too
  const faq = [
    {
      q: "What is the difference between the Major Arcana and the Minor Arcana?",
      a:
        "The Major Arcana describes milestones and deep lessons that shape identity and purpose. The Minor Arcana describes everyday situations through four suits called Wands, Cups, Swords, and Pentacles. In a reading the Major Arcana sets the overall theme while the Minor Arcana shows how the theme appears in daily life.",
    },
    {
      q: "Do I need to read reversed cards?",
      a:
        "Reading reversals is a choice. Some readers use reversals for additional nuance. Others read upright meanings and rely on context from the position and the question. Choose the approach that gives you the clearest guidance.",
    },
    {
      q: "Which spread should I start with?",
      a:
        "A single card pull builds daily consistency. A three card spread such as past, present, and future adds simple structure. When you feel ready, explore five card spreads and the Celtic Cross for more layers.",
    },
    {
      q: "How should I phrase a question for a reading?",
      a:
        "Use clear and open language. Ask about direction and next steps you can take. For example, you can ask what supports growth in a relationship or what actions will move a project forward. Avoid questions that demand a fixed prediction.",
    },
    {
      q: "Are these meanings a substitute for professional advice?",
      a:
        "No. Tarot offers reflection and emotional or spiritual insight. It does not replace medical, legal, or financial guidance. Please consult a qualified professional for those areas.",
    },
  ];

  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  const schemaGraph = schemaGraphFor(groupedCards, faqEntities);

  // Apply offset when landing with a hash or navigating hash changes
  useEffect(() => {
    if (window.location.hash) {
      requestAnimationFrame(() => scrollWithOffset(window.location.hash, false));
    }
    const onHashChange = () => scrollWithOffset(window.location.hash, true);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return (
    <div className={`${FONT} text-white min-h-screen relative overflow-hidden`} style={{ background: CANVAS_BG }}>
      {/* SEO (About.js-style via Seo component) */}
      <Seo
        title="Tarot Cards | Meanings and Symbols | TarotFate"
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={schemaGraph}
        keywords={megaKeywords}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">
          Explore the 78 Tarot Cards
        </h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>
          Tarot is a mirror that reflects pattern and choice. The Major Arcana contains 22 cards that describe the larger journey of growth and awakening. The four suits of Wands, Cups, Swords, and Pentacles each contain 14 cards that outline real situations and practical decisions across energy and ambition, emotion and relationship, thought and communication, and work and resources. Use this page to explore the full deck, compare images, and move directly to any card for upright and reversed meanings, symbolism, and clear guidance for love, career, and personal growth.
        </p>
      </section>

      {/* SUIT NAV */}
      <section className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-4 md:p-5`}>
          <div className="relative z-[1] flex flex-wrap gap-2 sm:gap-3 justify-center">
            {Object.keys(groupedCards).map((group) => {
              const href = `#${group.toLowerCase().replace(/\s+/g, "-")}`;
              return (
                <a
                  key={group}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    window.history.replaceState(null, "", href);
                    scrollWithOffset(href, true);
                  }}
                  className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition duration-100"
                >
                  ✦ {group}
                </a>
              );
            })}
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* SECTIONS */}
      <div className={`mt-8 space-y-12 ${WRAP} ${MAXW}`}>
        {Object.entries(groupedCards).map(([groupName, cards]) => (
          <section key={groupName} id={groupName.toLowerCase().replace(/\s+/g, "-")}>
            <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
              <div className="relative z-[1]">
                <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ {groupName}</h2>
                <p className={`${TEXT} text-sm leading-relaxed mt-2`}>
                  {suitIntro(groupName)}
                </p>

                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                  {cards.map((card) => (
                    <Link
                      key={card.slug}
                      to={`/tarot-card/${card.slug}`}
                      className="group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 hover:-translate-y-1 transition-transform duration-100"
                    >
                      <div className="p-2 flex flex-col items-center gap-2">
                        {/* Card image on top, fully visible (no crop) */}
                        <div className="w-full aspect-[2/3] rounded-lg bg-white/5 border border-white/10 overflow-hidden">
                          <img
                            src={card.image || `/card/${card.slug}.webp`}
                            alt={card.name}
                            className="w-full h-full object-contain select-none pointer-events-none"
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        </div>

                        {/* Title + subtitle below the card */}
                        <div className="text-center">
                          <h3 className="text-sm font-semibold text-white/95">{card.name}</h3>
                          {card.subtitle ? (
                            <p className="text-xs italic text-white/75">{card.subtitle}</p>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </div>
          </section>
        ))}
      </div>

      {/* FAQ */}
      <section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
        <div className="text-center">
          <h2 className="text-base sm:text-lg font-medium">Frequently Asked Questions</h2>
        </div>

        <div className="mt-6 rounded-sm border border-white/35 bg-white/5 divide-y divide-white/25">
          {[
            {
              q: "How can I start learning the Tarot cards?",
              a:
                "Begin with a daily single card pull. Write a short note about the image and the message you feel. Then read the meaning on the card page and compare your notes. Over time you will trust your own voice as much as the guide.",
            },
            {
              q: "What is the best way to use these suit pages?",
              a:
                "Use the suit pages to study cards that share a theme. Read the introductions to understand the focus of the suit. Then open a few cards and notice how the scenes relate to one another. Patterns will become clear and readings will feel easier.",
            },
            {
              q: "Do I need to memorize every meaning?",
              a:
                "Memorization helps, but it is not the only path. Learn core keywords and symbolism. Let the image speak. Use the question and the position in the spread to guide your interpretation.",
            },
            {
              q: "Should I read reversed meanings?",
              a:
                "Use reversals if they help you see nuance. If you prefer, read upright meanings and rely on context from the position and the question. Choose one approach and apply it with consistency.",
            },
            {
              q: "Are these interpretations professional advice?",
              a:
                "No. The information on this site supports reflection and personal growth. It does not replace medical, legal, or financial advice. Please consult a licensed professional when you need one.",
            },
          ].map(({ q, a }, i) => (
            <details key={i} className="group">
              <summary
                className="
                  cursor-pointer list-none px-4 md:px-6 py-3
                  text-sm sm:text-sm text-white/95 font-medium
                  flex items-start justify-between gap-6
                  after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none
                  group-open:after:content-['−']
                "
              >
                <span className="pr-2">{q}</span>
              </summary>
              <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>{a}</div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
